"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.maskSensitiveData = void 0;
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const intl_logger_1 = __importDefault(require("@rbilabs/intl-logger"));
/**
 * maskSensitiveData function uses these paths to mask the respective value
 * Usage:
 * Exact Key Matching: If 'email' is specified as a sensitive path, any email field in the object (at any level) will be masked.
 * Wildcard Matching: '*.customer.name' will match the name field inside any customer object, regardless of nesting.
 */
const defaultSensitivePaths = [
    'email',
    'appleEmail',
    'phoneNumber',
    'phone',
    'firstName',
    'customerName',
    '*.customer.name',
    '*.customer.username',
    '*.deliveryAddress.formattedAddress',
    '*.deliveryAddress.postalCode',
    '*.deliveryAddress.locality',
    '*.deliveryAddress.sublocality',
    '*.deliveryAddress.route',
    '*.deliveryAddress.streetNumber',
    '*.coordinates.latitude',
    '*.coordinates.longitude',
    '*.userDetails.name',
    '*.userDetails.dob',
    '*.dropoff.addressLine1',
    '*.dropoff.addressLine2',
    '*.dropoff.city',
    '*.dropoff.latitude',
    '*.dropoff.longitude',
    '*.dropoff.route',
    '*.dropoff.zip',
    '*.dropoff.streetNumber',
    '*.dropoff.city',
];
const MASK_CHAR_LENGTH = 3;
/**
 * Checks whether a given string is a valid JSON object.
 */
const tryParseJSON = (value) => {
    try {
        const parsed = JSON.parse(value);
        return typeof parsed === 'object' && parsed !== null ? parsed : null;
    }
    catch (_a) {
        return null;
    }
};
/**
 * Masks a string value keeping first 3 characters.
 */
const maskValue = (value) => {
    if (typeof value === 'number') {
        value = value.toString();
    }
    const trimmed = value === null || value === void 0 ? void 0 : value.trim();
    if (!trimmed)
        return value;
    return trimmed.length <= MASK_CHAR_LENGTH
        ? '*'.repeat(trimmed.length)
        : trimmed.slice(0, MASK_CHAR_LENGTH) + '*'.repeat(trimmed.length - MASK_CHAR_LENGTH);
};
/**
 * Parses sensitive paths into segments (used for wildcard matching)
 */
const parseSensitivePaths = (paths) => {
    const exactKeys = new Set();
    const wildcardPaths = [];
    for (const path of paths) {
        if (path.includes('.')) {
            wildcardPaths.push(path.split('.'));
        }
        else {
            exactKeys.add(path);
        }
    }
    return { exactKeys, wildcardPaths };
};
/**
 * This function matches the current path of a key in a nested object against a list of wildcard-sensitive paths
 * currentPath: an array of strings representing the full path to the current key while traversing.
 * E.g. for root.data.customer.name, this could be ["root", "data", "customer", "name"]
 * wildcardPath: a sensitive path split by . into segments. It can contain * (wildcard).
 * For "*.customer.name" → ["*", "customer", "name"]
 */
const matchesWildcardPath = (wildcardPath, currentPath) => {
    const wildcardSuffix = wildcardPath.slice(wildcardPath.findIndex((p) => p !== '*'));
    if (currentPath.length < wildcardSuffix.length) {
        return false;
    }
    for (let i = 1; i <= wildcardSuffix.length; i++) {
        const wildcardSegment = wildcardSuffix[wildcardSuffix.length - i];
        const currentSegment = currentPath[currentPath.length - i];
        if (wildcardSegment !== '*' && wildcardSegment !== currentSegment) {
            return false;
        }
    }
    return true;
};
/**
 * Recursive function to deeply mask sensitive fields.
 */
const deepMask = (obj, currentPath, exactKeys, wildcardPaths, visited = new WeakSet()) => {
    if (obj && typeof obj === 'object') {
        if (visited.has(obj))
            return obj; // avoid infinite recursion
        visited.add(obj);
    }
    if (obj instanceof Error) {
        const errorPlain = {
            message: obj.message,
            name: obj.name,
            stack: obj.stack,
        };
        // serializing and preserving custom properties
        const errorObj = obj;
        for (const key of Object.keys(errorObj)) {
            if (!(key in errorPlain)) {
                errorPlain[key] = errorObj[key];
            }
        }
        return deepMask(errorPlain, currentPath, exactKeys, wildcardPaths, visited);
    }
    if (typeof obj === 'string') {
        const parsed = tryParseJSON(obj);
        if (parsed) {
            const masked = deepMask(parsed, currentPath, exactKeys, wildcardPaths, visited);
            return JSON.stringify(masked);
        }
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map((item, index) => deepMask(item, [...currentPath, index.toString()], exactKeys, wildcardPaths, visited));
    }
    if (typeof obj === 'object' && obj !== null) {
        const newObj = {};
        for (const key in obj) {
            if (!Object.prototype.hasOwnProperty.call(obj, key))
                continue;
            const val = obj[key];
            const nextPath = [...currentPath, key];
            const isExactMatch = exactKeys.has(key);
            const isWildcardMatch = wildcardPaths.some((p) => matchesWildcardPath(p, nextPath));
            if (isExactMatch || isWildcardMatch) {
                newObj[key] = maskValue(val);
            }
            else {
                newObj[key] = deepMask(val, nextPath, exactKeys, wildcardPaths, visited);
            }
        }
        return newObj;
    }
    return obj;
};
/**
 * Masks sensitive data in the input before sending it to the logger service
 */
function maskSensitiveData(data, sensitivePaths = defaultSensitivePaths) {
    //deep cloning the input
    const clonedInput = cloneDeep_1.default(data);
    const { exactKeys, wildcardPaths } = parseSensitivePaths(sensitivePaths);
    try {
        return deepMask(clonedInput, [], exactKeys, wildcardPaths);
    }
    catch (error) {
        intl_logger_1.default.error({ error }, 'Error masking sensitive fields before logging');
        return data;
    }
}
exports.maskSensitiveData = maskSensitiveData;
//# sourceMappingURL=mask-sensitive-data.js.map